import { Component, OnDestroy, OnInit } from '@angular/core';
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { User } from '@app/core/interfaces';
import { AppState } from '@app/core/store/app.state';
import { selectUser } from '@app/core/store/userAuth/userAuth.selectors';
import { PortalRequestListComponent } from '../../components/portal-request-list/portal-request-list.component';
import { WebResponse } from '@app/shared/interfaces/webResponse.interface';
import { Store } from '@ngrx/store';
import { asyncScheduler, observeOn, Subject, takeUntil } from 'rxjs';
import { selectAdminDashboardLoading, selectUnconfirmedPortalRequests } from '../../store/admin.selectors';
import { PortalRequest } from '../../interfaces/portalRequest.interface';
import { ToastService } from '@app/core/services/toast.service';
import { UserListComponent } from '../../components/user-list/user-list.component';
import {
  GenericDashboardComponent,
  GenericDashboardTabConfig
} from '@app/shared/components/generic-dashboard/generic-dashboard.component';
import {ProviderMgmtComponent} from "@app/features/adminDashboard/components/provider-mgmt/provider-mgmt.component";

@Component({
  selector: 'app-admin-dashboard',
  imports: [
    GenericDashboardComponent
  ],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.scss'
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  readonly pageTitle = 'partner portal admin';
  userData!: User;
  dashboardType = DashboardType.Admin;
  unconfirmedPortalRequests: PortalRequest[] = [];
  customTabConfigs: GenericDashboardTabConfig[] = [];
  ngDestroy$ = new Subject<boolean>();
  loading = false;
  selectedTabIndex = 0;

  private readonly defaultTabContainerClass = 'overflow-x-auto w-[99%] mt-4 mb-8 border-2 p-4 rounded-lg ml-auto mr-auto bg-white';
  private readonly portalRequestListComponent = PortalRequestListComponent;
  private readonly userListComponent = UserListComponent;
  private readonly providerManagementComponent = ProviderMgmtComponent;

  constructor(
    private store: Store<AppState>,
    private toastService: ToastService
  ) {
  }

  ngOnInit(): void {
    this.syncTabConfigs();
    this.subscribeToUser();
    this.subscribeToLoading();
    this.subscribeToUnconfirmedPortalRequests();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  subscribeToUser() {
    this.store.select(selectUser)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((user: User | null) => {
        if (user) { this.userData = user; }
      });
  }

  subscribeToUnconfirmedPortalRequests() {
    this.store.select(selectUnconfirmedPortalRequests)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((response: WebResponse<PortalRequest[]> | null) => {
        if (response) {
          if (response.succeeded) {
            this.unconfirmedPortalRequests = response.data;
            this.syncTabConfigs();
          } else {
            this.toastService.showErrorToast('Failed to load unconfirmed portal requests! Please try again later.');
          }
        }
      });
  }

  subscribeToLoading() {
    this.store.select(selectAdminDashboardLoading)
      .pipe(
        observeOn(asyncScheduler),
        takeUntil(this.ngDestroy$)
      )
      .subscribe((loading: boolean) => {
        this.loading = loading;
      });
  }

  onSelectedTabIndexChange(selectedTabIndex: number) {
    this.selectedTabIndex = selectedTabIndex;
  }

  private syncTabConfigs(): void {
    this.customTabConfigs = [
      {
        key: 'dashboard',
        label: 'Portal Requests',
        component: this.portalRequestListComponent,
        inputs: {
          title: 'Portal Requests',
          dataSource: this.unconfirmedPortalRequests
        },
        containerClass: this.defaultTabContainerClass
      },
      {
        key: 'user-management',
        label: 'User Management',
        component: this.userListComponent,
        inputs: {
          title: 'Users'
        },
        containerClass: this.defaultTabContainerClass,
        renderWhenActiveOnly: true
      },
      {
        key: 'provider-management',
        label: 'Provider Management',
        component: this.providerManagementComponent,
        containerClass: this.defaultTabContainerClass,
        renderWhenActiveOnly: true
      }
    ];
  }
}
